"use client"

import { CareerProps } from "@/(admin)/modules/ui-builder/config/themes/tamda-one/Career"
import { CareerSkeleton } from "@/(admin)/modules/ui-builder/config/themes/tamda-one/Career/components/CareerSkeleton"
import JobDetail from "@/(admin)/modules/ui-builder/config/themes/tamda-one/Career/components/JobDetail"
import LocationSelect from "@/(admin)/modules/ui-builder/config/themes/tamda-one/Career/components/LocationSelect"
import { useUIBuilderLocaleStore } from "@/(admin)/modules/ui-builder/store/useUIBuilderLocaleStore"
import { Images } from "@/constants/images"
import { useCareers } from "@/hook/useCareers"
import { getFixedT } from "@/languages/i18n"
import { ComponentConfig } from "@measured/puck"
import Image from "next/image"
import { useParams } from "next/navigation"
import { useEffect, useMemo, useRef, useState } from "react"
import { twMerge } from "tailwind-merge"
import { generateResponsiveCSS } from "../../../lib/helper"
import "./styles.css"

export const RenderConfig: ComponentConfig<CareerProps> = {
  render: ({ className, styles, responsiveType }) => {
    const params = useParams()
    const storeLocale = useUIBuilderLocaleStore(state => state.locale)
    const id = `tamda-group-career-${Math.random().toString(36).substr(2, 9)}`
    const responsiveCSS = generateResponsiveCSS(id, styles || {}, responsiveType)
    // Public có params.locale; editor thì không -> rơi về locale của blockContent đang sửa.
    const locale = (params?.locale as string) || storeLocale || "vi"
    const t = getFixedT(locale)
    const { careers, loading, error, pagination, fetchCareers, setLocale } = useCareers({ sort: "createdAt:asc" })
    const [selectedLocation, setSelectedLocation] = useState<string | undefined>(undefined)
    const [selectedJobType, setSelectedJobType] = useState<string | undefined>(undefined)
    const [expandedMap, setExpandedMap] = useState<Record<string, boolean>>({})
    const prevLocaleRef = useRef<string>(locale)

    const locationOptions = {
      label: t("Select a branch"),
      value: "all",
      options: [
        { label: t("All"), value: "all" },
        { label: "Brno", value: "brno" },
        { label: "Praha", value: "praha" },
        { label: "Usti", value: "usti" },
      ],
    }

    const jobTypeOptions = {
      label: t("Select a job type"),
      value: "all",
      options: [
        { label: t("All"), value: "all" },
        { label: t("Full-time"), value: "fulltime" },
        { label: t("Part-time"), value: "parttime" },
        { label: t("Remote"), value: "remote" },
      ],
    }

    const handleLocationChange = (value: string | undefined) => {
      setSelectedLocation(value)
    }

    const handleJobTypeChange = (value: string | undefined) => {
      setSelectedJobType(value)
    }

    const filters = useMemo(() => {
      const result: Record<string, any> = {}

      if (selectedLocation && selectedLocation !== "all") {
        result.branch = selectedLocation
      }

      if (selectedJobType && selectedJobType !== "all") {
        result.jobType = selectedJobType
      }

      return result
    }, [selectedLocation, selectedJobType])

    const handlePageChange = (page: number) => {
      fetchCareers(locale, filters, {
        page: page,
        pageSize: pagination?.pageSize || 10,
      })
    }

    useEffect(() => {
      const localeChanged = prevLocaleRef.current !== locale

      if (localeChanged) {
        setLocale(locale)
        prevLocaleRef.current = locale
        fetchCareers(locale, filters)
      } else {
        fetchCareers(locale, filters)
      }
    }, [locale, filters])

    return (
      <>
        {responsiveCSS}
        <div className={twMerge(className)} id={id}>
          <div className="container mx-auto max-w-[540px] sm:max-w-[720px] md:max-w-[960px] lg:max-w-[1290px] px-[15px]">
            <div className="flex flex-col sm:flex-row mt-20 w-full justify-between">
              <h5 className="text-2xl leading-[26px] font-black mb-[15px]">{t("Open Positions")}</h5>

              <div className="flex items-center gap-[6px] sm:gap-[10px] justify-between sm:justify-end">
                <p className="text-base text-[#333333] pr-2 sm:pr-10 shrink-0">{t("Filter")}</p>

                <div className="flex items-center gap-[6px] sm:gap-[10px] flex-1 min-w-0 sm:flex-initial sm:min-w-auto">
                  <LocationSelect
                    value={selectedLocation}
                    onChange={(value?: string) => handleLocationChange(value)}
                    options={locationOptions.options}
                    placeholder={locationOptions.label}
                    loading={loading}
                    className="flex-1 min-w-0 sm:flex-initial"
                    locale={locale}
                  />

                  <LocationSelect
                    value={selectedJobType}
                    onChange={(value?: string) => handleJobTypeChange(value)}
                    options={jobTypeOptions.options}
                    placeholder={jobTypeOptions.label}
                    loading={loading}
                    className="flex-1 min-w-0 sm:flex-initial"
                    locale={locale}
                  />
                </div>
              </div>
            </div>
            {loading && <CareerSkeleton />}
            {careers && careers.length > 0 ? (
              <div className="flex flex-col">
                {careers.map(career => (
                  <div key={career.id} className="mt-5 pb-5 border-b border-[rgba(0, 0, 0, 0.1)]">
                    <div className="grid grid-cols-1 sm:grid-cols-12">
                      <div className="col-span-5 sm:pr-[15px] flex flex-col sm:gap-[15px] gap-[5px]">
                        <h5 className="mb-[10px] sm:mb-0 text-2xl leading-[26px] font-black text-[#212529]">
                          {career?.attributes?.position}
                        </h5>
                        <div className="flex items-center gap-[10px]">
                          <Image src={Images.iconJobCareer} alt="icon job" width={16} height={16} />
                          <span className="text-base text-[#333333]">
                            {t("Job Type")}: {career?.attributes?.jobType}
                          </span>
                        </div>
                        <div className="flex items-center gap-[10px]">
                          <Image src={Images.iconSalaryCareer} alt="icon salary" width={16} height={16} />
                          <span className="text-base text-[#333333]">
                            {t("Salary")}: {career?.attributes?.salary}
                          </span>
                        </div>
                        <div className="flex items-center gap-[10px]">
                          <Image src={Images.iconCalendarCareer} alt="icon expired date" width={16} height={16} />
                          <span className="text-base text-[#333333]">
                            {t("Exp date")}: {career?.attributes?.expiredDate}
                          </span>
                        </div>
                        <div className="flex items-center gap-[10px]">
                          <Image src={Images.iconJobCareer} alt="icon job" width={16} height={16} />
                          <span className="text-base text-[#333333]">
                            {t("Quantity")}: {career?.attributes?.quantity}
                          </span>
                        </div>
                      </div>
                      <div className="col-span-5 sm:px-[15px] mt-5 sm:mt-0 flex gap-[30px] items-center">
                        <div className="flex items-center h-[100px] w-[100px] p-2 rounded-sm border border-[rgba(0, 0, 0, 0.2)]">
                          <Image
                            src={Images.brandCareer}
                            alt="brand"
                            width={82}
                            height={57}
                            className="h-[57px] w-[82px]"
                          />
                        </div>
                        <div className="flex flex-col gap-[15px]">
                          <h6 className="text-xl leading-[22px] text-[#212529] font-bold">
                            {career?.attributes?.company}
                          </h6>
                          <div className="flex items-center gap-[10px]">
                            <Image
                              src={Images.iconLocationCareer}
                              alt="icon location"
                              width={16}
                              height={16}
                              className="h-[16px] w-[16px]"
                            />
                            <span className="text-base text-[#333333]">
                              {t("Location")}: {career?.attributes?.location}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="col-span-2 sm:pl-[15px] mt-5 sm:mt-0 flex items-center">
                        <button
                          onClick={() =>
                            setExpandedMap(prev => ({
                              ...prev,
                              [career?.id?.toString() || ""]: !prev[career?.id?.toString() || ""],
                            }))
                          }
                          className={`axil-button btn-large ${expandedMap[career?.id?.toString() || ""] ? "btn-primary" : "btn-transparent"
                            }`}
                        >
                          <span className="button-text">{t("Apply Now")}</span>
                          <span className="button-icon"></span>
                        </button>
                      </div>
                    </div>
                    {expandedMap[career?.id?.toString() || ""] && <JobDetail data={career} locale={locale} />}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center text-[#333333] min-h-[200px] text-base">
                {t("No results found")}
              </div>
            )}
            <div className="mt-[70px]">
              <ul className="flex items-center justify-center pb-0 sm:pb-[2.5625rem]">
                {Array.from({ length: pagination?.pageCount || 0 }).map((_, index) => (
                  <li key={index} className="mx-[3px]">
                    <button
                      onClick={() => handlePageChange(index + 1)}
                      className="min-w-[42px] px-[15px] text-base leading-[42px] rounded-lg flex items-center justify-center font-semibold transition-all duration-300 bg-[#157e40] text-white border border-[#157e40]"
                    >
                      {index + 1}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </>
    )
  },
}

export default RenderConfig
